import React from "react";
import { useTranslation } from "react-i18next";
import { subcategoryLabelsMap } from "../../../utils/categoryLabels";

const SectionCard = ({ children, title, icon }) => (
  <div style={{
    background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid var(--card-border)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div style={{
        width: "40px",
        height: "40px",
        borderRadius: "12px",
        background: "rgba(59,130,246,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "20px",
        color: "#3b82f6"
      }}>
        {icon}
      </div>
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const MetricChip = ({ label, value, color }) => (
  <div style={{
    padding: "10px 14px",
    borderRadius: "999px",
    border: `1px solid ${color}33`,
    background: `${color}14`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    flex: "1 1 0",
    minWidth: "160px"
  }}>
    <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)" }}>
      {label}
    </span>
    <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
      {value}
    </span>
  </div>
);

const getResponseTimeGradient = (time) => {
  if (typeof time !== "number" || time === 0) return "linear-gradient(135deg,#1e3a8a,#3b82f6)";
  if (time <= 1) return "linear-gradient(135deg,#065f46,#22c55e)"; // Green for fast
  if (time <= 3) return "linear-gradient(135deg,#92400e,#f59e0b)"; // Orange for medium
  return "linear-gradient(135deg,#7f1d1d,#dc2626)"; // Red for slow
};

const ReportRenderer = ({ data }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_detailed.rto" });
  const rtoData = data || {};

  const formatNumber = (value) => {
    if (typeof value !== "number") return t("notAvailable");
    if (value >= 1000) return value.toLocaleString();
    return Number.isInteger(value) ? value : value.toFixed(2);
  };

  const formatTime = (seconds) => {
    if (typeof seconds !== "number") return t("notAvailable");
    if (seconds === 0) return t("time.zeroSeconds");
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}${t("time.msUnit")}`;
    if (seconds < 60) return `${seconds.toFixed(2)}${t("time.sUnit")}`;
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(2);
    return `${mins}${t("time.mUnit")} ${secs}${t("time.sUnit")}`;
  };
  
  // Extract data from rto_results structure
  const totalTests = rtoData.total_tests || 0;
  const totalPassed = rtoData.total_passed || 0;
  const totalFailed = rtoData.total_failed || 0;
  const avgResponseTime = rtoData.avg_response_time || 0;
  
  const subCatCounts = rtoData.sub_cat_counts || {};
  const subCatAvgTime = rtoData.sub_cat_avg_time || {};
  
  // Category labels mapping
  const categoryLabels = {
    reg_size_prompts: subcategoryLabelsMap.reg_size_prompts || "Regular-size Prompts",
    mid_size_prompts: subcategoryLabelsMap.mid_size_prompts || "Medium-size Prompts",
    large_size_prompts: subcategoryLabelsMap.large_size_prompts || "Large-size Prompts",
    doc_upload_tests: subcategoryLabelsMap.doc_upload_tests || "Document Upload Tests"
  };
  
  // Define category order (preferred order)
  const categoryOrder = ['reg_size_prompts', 'mid_size_prompts', 'large_size_prompts', 'doc_upload_tests'];
  
  // Dynamically determine which categories exist in the data
  // Check sub_cat_counts.total_tests to see which categories have data
  const availableCategories = categoryOrder.filter(catKey => {
    const hasCounts = subCatCounts?.total_tests?.[catKey] !== undefined;
    const hasAvgTime = subCatAvgTime?.[catKey] !== undefined;
    return hasCounts || hasAvgTime;
  });
  
  // Use available categories, or fallback to all categories if data structure is different
  const categories = availableCategories.length > 0 ? availableCategories : categoryOrder;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px" }}>
      <SectionCard title={t("responseTimeOverview")} icon={<i className="fas fa-clock"></i>}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{
            background: getResponseTimeGradient(avgResponseTime),
            borderRadius: "12px",
            padding: "18px",
            color: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center"
          }}>
            <div style={{ opacity: 0.8, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{t("averageResponseTime")}</div>
            <div style={{ fontSize: "32px", fontWeight: 700, marginTop: "4px" }}>
              {formatTime(avgResponseTime)}
            </div>
            <div style={{ marginTop: "12px", fontSize: "14px", opacity: 0.9 }}>
              {t("totalTests")}: {formatNumber(totalTests)} · {t("passed")}: {formatNumber(totalPassed)} · {t("failed")}: {formatNumber(totalFailed)}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
            <MetricChip label={t("totalTests")} value={formatNumber(totalTests)} color="#3b82f6" />
            <MetricChip label={t("passed")} value={formatNumber(totalPassed)} color="#22c55e" />
            <MetricChip label={t("failed")} value={formatNumber(totalFailed)} color="#ef4444" />
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t("categoryBreakdown")} icon={<i className="fas fa-chart-bar"></i>}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
          {categories.map((categoryKey) => {
            const categoryLabel = categoryLabels[categoryKey] || categoryKey;
            const totalTestsForCat = subCatCounts?.total_tests?.[categoryKey] || 0;
            const totalPassedForCat = subCatCounts?.total_passed?.[categoryKey] || 0;
            const totalFailedForCat = subCatCounts?.total_failed?.[categoryKey] || 0;
            const avgTimeForCat = subCatAvgTime?.[categoryKey] || 0;
            const passRate = totalTestsForCat > 0 ? (totalPassedForCat / totalTestsForCat) * 100 : 0;

            return (
              <div key={categoryKey} style={{
                border: "1px solid var(--card-border)",
                borderRadius: "12px",
                padding: "16px",
                background: "var(--card-bg)",
                display: "flex",
                flexDirection: "column",
                gap: "12px"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontWeight: 600, color: "var(--card-text)", fontSize: "15px" }}>
                    {categoryLabel}
                  </div>
                  <span style={{
                    padding: "4px 10px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    background: "rgba(59,130,246,0.12)",
                    color: "#2563eb",
                    fontWeight: 600
                  }}>
                    {totalTestsForCat} {totalTestsForCat === 1 ? t("testChip") : t("testsChip")}
                  </span>
                </div>
                
                <div style={{
                  background: getResponseTimeGradient(avgTimeForCat),
                  borderRadius: "8px",
                  padding: "12px",
                  color: "white",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "11px", opacity: 0.9, textTransform: "uppercase", letterSpacing: "0.08em" }}>{t("avgResponseTime")}</div>
                  <div style={{ fontSize: "24px", fontWeight: 700, marginTop: "4px" }}>
                    {formatTime(avgTimeForCat)}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span style={{ fontSize: "11px", color: "var(--muted-text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("totalTests")}</span>
                      <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)" }}>{totalTestsForCat}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span style={{ fontSize: "11px", color: "var(--muted-text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("passRate")}</span>
                      <span style={{ fontSize: "14px", fontWeight: 600, color: passRate >= 70 ? "#22c55e" : passRate >= 40 ? "#f59e0b" : "#ef4444" }}>
                        {passRate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "4px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span style={{ fontSize: "11px", color: "var(--muted-text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("passed")}</span>
                      <span style={{ fontSize: "14px", fontWeight: 600, color: "#22c55e" }}>{totalPassedForCat}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span style={{ fontSize: "11px", color: "var(--muted-text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("failed")}</span>
                      <span style={{ fontSize: "14px", fontWeight: 600, color: "#ef4444" }}>{totalFailedForCat}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

    </div>
  );
};

const RtoTabDetails = ({ rtoData, keyExists, isEmptyObject, hasExpectedStructure }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_detailed.rto" });

  if (!keyExists || isEmptyObject) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(107, 114, 128, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-file-alt" style={{ fontSize: "28px", color: "#6b7280" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("noReportAvailable")}
        </div>
      </div>
    );
  }

  if (!hasExpectedStructure) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(239, 68, 68, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: "28px", color: "#ef4444" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("invalidStructure")}
        </div>
      </div>
    );
  }

  return <ReportRenderer data={rtoData} />;
};

export default RtoTabDetails;
